"use client";

import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, Briefcase, Map, CircleCheck } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const differentiators = [{
  title: "Personalized Travel Support",
  description: "Our team provides personalized assistance throughout the travel planning process. Clients can contact us through our official communication channels for questions, booking inquiries, and travel-related assistance.",
  icon: ShieldCheck,
  tag: "Dedicated Client Care"
}, {
  title: "One-to-One Travel Planning",
  description: "Experience personalized service with a dedicated travel agent who understands your preferences, ensuring every journey is tailored to you.",
  icon: Briefcase,
  tag: "One-to-One Travel Planning"
}, {
  title: "Travel Guidance & Assistance",
  description: "We help clients prepare for their journeys with practical travel information, personalized planning, and guidance on contacting relevant travel suppliers when assistance is needed.",
  icon: Map,
  tag: "Personalized Travel Assistance"
}];
export default function Vipconciergecare() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="261050" id="vipconciergecare" className="py-16 md:py-24 bg-[#1a365d] text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerReveal.ref} className={`max-w-3xl mx-auto text-center transition-all duration-700 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-4 text-sm px-3 py-1 bg-white/10 text-white border-white/20 backdrop-blur-md">
            Dedicated Client Care
          </Badge>
          <h2 className="text-3xl md:text-5xl font-default font-bold text-white mb-6 tracking-tight">
            Quiet Luxury, Personal Connection
          </h2>
          <p className="text-lg text-white/80 font-sans leading-relaxed">
            Our bespoke concierge service offers LGBTQ+ travelers personalized, one-to-one support throughout the travel planning process.
          </p>
        </div>

        <div ref={gridReveal.ref} className={`mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-700 delay-200 ease-out ${gridReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {differentiators.map((item, i) => {
          const Icon = item.icon;
          return <Card key={i} data-index={i} className="relative overflow-hidden border border-white/10 shadow-lg hover:shadow-xl transition-all duration-300 bg-white/5 backdrop-blur-[20px] rounded-lg">
                {}
                <div className="absolute top-0 left-0 w-full h-1.5 bg-white/20" />
                
                <CardHeader className="pb-4 pt-8">
                  <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-xl font-default text-white">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-white/70 font-sans leading-relaxed">
                    {item.description}
                  </p>
                  <div className="mt-6 flex items-center gap-2 text-sm font-medium text-white/90">
                    <CircleCheck className="w-4 h-4 text-white/60" />
                    <span>{item.tag}</span>
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}